/**
 * AiFeaturesSettings.tsx — per-shop AI master toggle + capability toggles + provider API keys.
 *
 * AI is opt-in per shop. When the master toggle is off (or no key is configured) the
 * rest of the app is unaffected and AI affordances stay hidden. Keys are entered here
 * and stored AES-256-GCM encrypted on this shop instance only (never sent externally).
 *
 * Capability toggles (migration 195) — ai_lookup_enabled, ai_diagnostic_procedures_enabled,
 * ai_dtc_diagnosis_enabled — replace the old build-time-only React env flags
 * (REACT_APP_AI_LOOKUP / REACT_APP_DIAGNOSTIC_PROCEDURES / REACT_APP_DTC_DIAGNOSIS),
 * which were unset in this deployment and gave the owner no way to turn these features
 * on. Each is independently opt-in (default off) and additionally requires the master
 * toggle to be on — the server enforces both via requireAiFeature(...); this UI only
 * reflects that state.
 *
 * Endpoints:
 *   GET    /api/ai-settings                 — { ai_enabled, ai_ready, ai_lookup_enabled,
 *                                                ai_diagnostic_procedures_enabled,
 *                                                ai_dtc_diagnosis_enabled, ai_model_tier, keys }
 *   PUT    /api/ai-settings                 — { ai_enabled?, ai_lookup_enabled?,
 *                                                ai_diagnostic_procedures_enabled?,
 *                                                ai_dtc_diagnosis_enabled?, ai_model_tier? }
 *   PUT    /api/ai-settings/keys/:provider  — { apiKey }
 *   DELETE /api/ai-settings/keys/:provider
 *
 * OpenRouter is the recommended path: one key, mapped by quality tier (auto/fast/balanced/best)
 * to whatever model the backend picks for each job — the owner never chooses model names.
 * 'auto' is the default and a real choice, not a placeholder: it's stored as NULL server-side
 * and means each job keeps its own sensible default (cheap models for high-volume lookups,
 * the best model for writing estimate text). Reporting the unset state as 'balanced' would
 * silently downgrade estimate writing on the next save, so the UI must send 'auto' explicitly
 * rather than treating it as an absence of a value.
 * Direct Groq/Anthropic/OpenAI keys remain supported (shops already using them keep working)
 * but are presented as the fallback, collapsed under "Use my own provider keys instead".
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Switch,
  Input,
  Button,
  Typography,
  Space,
  Tag,
  Alert,
  Divider,
  Spin,
  Radio,
  App as AntdApp,
} from 'antd';
import { RobotOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph, Link } = Typography;

type AiProvider = 'groq' | 'openai' | 'anthropic' | 'openrouter';
type AiModelTier = 'auto' | 'fast' | 'balanced' | 'best';

interface ProviderKeyStatus {
  configured: boolean;
  source: 'shop' | 'env' | null;
  last_set_at: string | null;
}

interface AiSettingsResponse {
  ai_enabled: boolean;
  ai_ready: boolean;
  ai_lookup_enabled: boolean;
  ai_diagnostic_procedures_enabled: boolean;
  ai_dtc_diagnosis_enabled: boolean;
  ai_model_tier: AiModelTier;
  keys: Record<AiProvider, ProviderKeyStatus>;
}

interface ProviderConfig {
  id: AiProvider;
  label: string;
  description: string;
  placeholder: string;
}

interface ModelTierConfig {
  id: AiModelTier;
  label: string;
  description: string;
}

const MODEL_TIERS: ModelTierConfig[] = [
  {
    id: 'auto',
    label: 'Automatic (recommended)',
    description: 'Cheaper models for quick lookups, the best model for writing estimates.',
  },
  { id: 'fast', label: 'Fast', description: 'Cheapest everywhere. Lowest quality on estimate text.' },
  { id: 'balanced', label: 'Balanced', description: 'One mid-range model for everything.' },
  { id: 'best', label: 'Best', description: 'Highest quality everywhere. Most expensive.' },
];

const OPENROUTER_PROVIDER: ProviderConfig = {
  id: 'openrouter',
  label: 'OpenRouter',
  description: 'One key covers every model CNF uses — recommended. With this set, the keys below are optional.',
  placeholder: 'sk-or-...',
};

type AiFeatureKey = 'ai_lookup_enabled' | 'ai_diagnostic_procedures_enabled' | 'ai_dtc_diagnosis_enabled';

interface FeatureConfig {
  id: AiFeatureKey;
  label: string;
  description: string;
}

const FEATURES: FeatureConfig[] = [
  {
    id: 'ai_lookup_enabled',
    label: 'Parts, recalls & TSB lookup',
    description: 'AI-assisted parts search, recalls, TSBs, and similar-fix suggestions on work order line items.',
  },
  {
    id: 'ai_diagnostic_procedures_enabled',
    label: 'Diagnostic procedure lookups',
    description: 'Spec lookups (compression, leakdown, etc.) and network fix-matching on the Diagnostics tab.',
  },
  {
    id: 'ai_dtc_diagnosis_enabled',
    label: 'DTC diagnosis',
    description: 'AI-assisted translation of OBD-II trouble codes (e.g. P0301) into likely causes.',
  },
];

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'groq',
    label: 'Groq',
    description: 'Fast, low-cost LLM inference (Llama models). Used for AI lookups and dispatch.',
    placeholder: 'gsk_...',
  },
  {
    id: 'anthropic',
    label: 'Anthropic (Claude)',
    description: 'Claude models — primary LLM for high-quality AI lookups.',
    placeholder: 'sk-ant-...',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    description: 'Embeddings for semantic fix-knowledge search (optional).',
    placeholder: 'sk-...',
  },
];

export const AiFeaturesSettings: React.FC = () => {
  const { message } = AntdApp.useApp();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<AiSettingsResponse | null>(null);
  const [toggling, setToggling] = useState(false);
  const [togglingFeature, setTogglingFeature] = useState<AiFeatureKey | null>(null);
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [savingTier, setSavingTier] = useState(false);
  const [showOwnKeys, setShowOwnKeys] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ai-settings');
      setSettings(res.data);
    } catch (err: any) {
      message.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  // If a shop already has a legacy provider key configured, don't hide it behind the
  // collapsed section by default — the owner needs to see what's actually in use.
  useEffect(() => {
    if (settings && PROVIDERS.some((p) => settings.keys?.[p.id]?.configured)) {
      setShowOwnKeys(true);
    }
  }, [settings]);

  const handleToggle = async (checked: boolean) => {
    setToggling(true);
    try {
      await api.put('/ai-settings', { ai_enabled: checked });
      message.success(checked ? 'AI features enabled' : 'AI features turned off');
      await load();
    } catch (err: any) {
      message.error('Failed to update AI toggle');
    } finally {
      setToggling(false);
    }
  };

  const handleToggleFeature = async (feature: AiFeatureKey, checked: boolean) => {
    setTogglingFeature(feature);
    try {
      await api.put('/ai-settings', { [feature]: checked });
      message.success(checked ? 'Feature enabled' : 'Feature turned off');
      await load();
    } catch (err: any) {
      message.error('Failed to update feature toggle');
    } finally {
      setTogglingFeature(null);
    }
  };

  const handleSaveKey = async (provider: AiProvider) => {
    const apiKey = (keyInputs[provider] ?? '').trim();
    if (!apiKey) {
      message.warning('Enter an API key first');
      return;
    }
    setSavingProvider(provider);
    try {
      await api.put(`/ai-settings/keys/${provider}`, { apiKey });
      message.success(`${provider} key saved`);
      setKeyInputs((prev) => ({ ...prev, [provider]: '' }));
      await load();
    } catch (err: any) {
      message.error(`Failed to save ${provider} key`);
    } finally {
      setSavingProvider(null);
    }
  };

  const handleRemoveKey = async (provider: AiProvider) => {
    setSavingProvider(provider);
    try {
      await api.delete(`/ai-settings/keys/${provider}`);
      message.success(`${provider} key removed`);
      await load();
    } catch (err: any) {
      message.error(`Failed to remove ${provider} key`);
    } finally {
      setSavingProvider(null);
    }
  };

  const handleTierChange = async (tier: AiModelTier) => {
    setSavingTier(true);
    try {
      await api.put('/ai-settings', { ai_model_tier: tier });
      message.success('Model quality updated');
      await load();
    } catch (err: any) {
      message.error('Failed to update model quality');
    } finally {
      setSavingTier(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  const aiEnabled = settings?.ai_enabled ?? false;

  const renderKeyCard = (p: ProviderConfig) => {
    const status = settings?.keys?.[p.id];
    return (
      <div key={p.id} data-testid={`ai-provider-${p.id}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Space size={8}>
            <Text strong>{p.label}</Text>
            {p.id === 'openrouter' && <Tag color="blue">Recommended</Tag>}
          </Space>
          {status?.configured ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              {status.source === 'env' ? 'Server default' : 'Key set'}
            </Tag>
          ) : (
            <Tag>Not set</Tag>
          )}
        </div>
        <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
          {p.description}
        </Paragraph>
        <Space.Compact style={{ width: '100%' }}>
          <Input.Password
            data-testid={`ai-key-input-${p.id}`}
            placeholder={p.placeholder}
            prefix={<LockOutlined />}
            value={keyInputs[p.id] ?? ''}
            onChange={(e) => setKeyInputs((prev) => ({ ...prev, [p.id]: e.target.value }))}
            disabled={!aiEnabled}
          />
          <Button
            data-testid={`ai-key-save-${p.id}`}
            type="primary"
            loading={savingProvider === p.id}
            disabled={!aiEnabled}
            onClick={() => handleSaveKey(p.id)}
          >
            Save
          </Button>
          {status?.source === 'shop' && (
            <Button
              data-testid={`ai-key-remove-${p.id}`}
              danger
              disabled={!aiEnabled}
              onClick={() => handleRemoveKey(p.id)}
            >
              Remove
            </Button>
          )}
        </Space.Compact>
      </div>
    );
  };

  return (
    <Card data-testid="ai-features-settings">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <RobotOutlined /> AI Features
            </Title>
            <Text type="secondary">Turn AI on and add your provider keys. Optional — the rest of the app works without it.</Text>
          </div>
          <Switch
            data-testid="ai-enabled-toggle"
            checked={aiEnabled}
            loading={toggling}
            onChange={handleToggle}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </div>

        {aiEnabled && !settings?.ai_ready && (
          <Alert
            type="warning"
            showIcon
            message="AI is on but no API key is configured"
            description="Add at least one provider key below for AI features to work."
          />
        )}

        {!aiEnabled && (
          <Alert
            type="info"
            showIcon
            message="AI features are off"
            description="No AI calls are made and AI options stay hidden. All other features work normally."
          />
        )}

        <Divider style={{ margin: '8px 0' }} />

        <div>
          <Title level={5} style={{ margin: 0 }}>Capabilities</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Turn on individual AI capabilities. Each also requires the master toggle above.
          </Text>
        </div>

        {FEATURES.map((f) => {
          const checked = !!settings?.[f.id];
          return (
            <div
              key={f.id}
              data-testid={`ai-feature-${f.id}`}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
            >
              <div>
                <Text strong>{f.label}</Text>
                <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                  {f.description}
                </Paragraph>
              </div>
              <Switch
                data-testid={`ai-feature-toggle-${f.id}`}
                checked={checked}
                disabled={!aiEnabled}
                loading={togglingFeature === f.id}
                onChange={(v) => handleToggleFeature(f.id, v)}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </div>
          );
        })}

        <Divider style={{ margin: '8px 0' }} />

        <div>
          <Title level={5} style={{ margin: 0 }}>Model quality</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            CNF picks the actual model for each job — pick how it should balance speed, cost, and quality.
          </Text>
        </div>

        <Radio.Group
          data-testid="ai-model-tier"
          value={settings?.ai_model_tier ?? 'auto'}
          disabled={!aiEnabled || savingTier}
          onChange={(e) => handleTierChange(e.target.value)}
        >
          <Space direction="vertical" size={4}>
            {MODEL_TIERS.map((t) => (
              <Radio key={t.id} data-testid={`ai-model-tier-${t.id}`} value={t.id}>
                <Text strong>{t.label}</Text>{' '}
                <Text type="secondary" style={{ fontSize: 12 }}>— {t.description}</Text>
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        <Divider style={{ margin: '8px 0' }} />

        <div>
          <Title level={5} style={{ margin: 0 }}>Provider key</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Recommended: one OpenRouter key covers every model CNF uses.{' '}
            <Link href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">
              Get a key at openrouter.ai/keys
            </Link>
            .
          </Text>
        </div>

        {renderKeyCard(OPENROUTER_PROVIDER)}

        <Button
          type="link"
          data-testid="ai-own-keys-toggle"
          onClick={() => setShowOwnKeys((v) => !v)}
          style={{ padding: 0, alignSelf: 'flex-start', height: 'auto' }}
        >
          {showOwnKeys ? 'Hide own provider keys' : 'Use my own provider keys instead'}
        </Button>

        {showOwnKeys && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Optional — only needed if you'd rather not use OpenRouter. Each key below covers just that one provider's models.
            </Text>
            {PROVIDERS.map((p) => renderKeyCard(p))}
          </Space>
        )}

        <Alert
          type="success"
          showIcon
          icon={<LockOutlined />}
          message="Keys are stored encrypted (AES-256-GCM) on this shop only and never shared externally."
        />
      </Space>
    </Card>
  );
};

export default AiFeaturesSettings;
